import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../common/Config';
import Swal from 'sweetalert2';
import modulesConfig from '../common/modulesConfig';
import { permissionsToCheckedMap } from '../../utils/permissionKeyResolver';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, Button, Paper, Alert, CircularProgress, Grid2 } from '@mui/material';
import '../common/commonStyles.css';

const ChangePermissions = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [checkedPermissions, setCheckedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [roleName, setRoleName] = useState('');
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const token = localStorage.getItem("token");
  // Fetch all available roles
  useEffect(() => {
    const fetchRoles = async () => {
      const accessToken = localStorage.getItem('accessToken');
      try {
        const response = await fetch(`${config.API_URL}/get_users_role`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch roles');
        }

        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          const formattedRoles = data.data.map(item => ({
            id: item.id,
            name: item.name || `Role ${item.id}`
          }));
          setRoleOptions(formattedRoles);
        }
      } catch (err) {
        console.error("Error fetching roles:", err);
        Swal.fire("Error", "Failed to load roles", "error");
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch permissions for selected role
  useEffect(() => {
    if (!roleId) {
      setLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      try {
        const res = await fetch(`${config.API_URL}/get-permissions/${roleId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });

        if (!res.ok) {
          throw new Error('Failed to fetch permissions');
        }

        const data = await res.json();
        if (data.success) {
          setCheckedPermissions(permissionsToCheckedMap(data.permissions, modulesConfig));

          if (data.role_name) {
            setRoleName(data.role_name);
          }
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [roleId]);

  const handleRoleChange = (e) => {
    const selectedRoleId = e.target.value;
    if (selectedRoleId) {
      navigate(`/settings/change_permissions/${selectedRoleId}`);
    }
  };

  const handleCheckboxChange = (permId, checked) => {
    setCheckedPermissions(prev => ({
      ...prev,
      [permId]: checked
    }));
  };

  const handleUpdate = async () => {
    const selectedPermissions = Object.keys(checkedPermissions).filter(key => checkedPermissions[key]);

    try {
      const formData = new FormData();
      formData.append('roleId', roleId);
      formData.append('permissions', JSON.stringify(selectedPermissions));

      const response = await fetch(`${config.API_URL}/update-permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        Swal.fire("Success", "Permissions updated!", "success");
      } else {
        Swal.fire("Error", result.message || "Failed to update permissions", "error");
      }
    } catch (err) {
      Swal.fire("Error", "API error.", "error");
    }
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px:2, py:1.2}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
              <Typography className="page-title">
                Role Permissions {roleName && `- ${roleName}`} {roleId && `(Role ID: ${roleId})`}
              </Typography>
              <FormControl fullWidth size="small" sx={{ maxWidth: 400 }}>
                <InputLabel id="role-select-label">Select Role</InputLabel>
                <Select 
                  className="input-border"
                  size="small"
                  labelId="role-select-label"
                  id="roleSelect"
                  value={roleId || ''}
                  onChange={handleRoleChange}
                  disabled={loadingRoles}
                  label="Select Role"
                >
                  <MenuItem value="">
                    <em>-- Select a Role --</em>
                  </MenuItem>
                  {roleOptions.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name} (ID: {role.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!roleId ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Role ID is required. Please access this page with a valid role ID.
                </Typography>
                <Typography variant="body2" component="code" sx={{ fontSize: '12px' }}>
                  Example: /settings/change_permissions/1
                </Typography>
              </Alert>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2, color: '#637381' }}>
                  Loading permissions...
                </Typography>
              </Box>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
             
                    border: '1px solid rgba(145, 158, 171, 0.16)',
                    overflow: 'hidden',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#EAEAEA',
                      borderBottom: '1px solid #c4c4c4',
                      p: 1.5,
                    }}
                  >
                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 12, md: 3 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
                          Module Name
                        </Typography>
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 9 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
                          Permissions
                        </Typography>
                      </Grid2>
                    </Grid2>
                  </Box>
                  {modulesConfig.map((module, index) => (
                    <Box
                      key={module.name}
                      sx={{
                        borderBottom: index < modulesConfig.length - 1 ? '1px dashed #c4c4c4' : 'none',
                        p: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    >
                      <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 3 }}>
                          <Typography className="commonColor" sx={{ fontWeight: 500, fontSize: '14px'}}>
                            {module.name}
                          </Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 9 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
                            {module.permissions.map((perm) => (
                              <FormControlLabel
                                key={perm.id}
                                control={
                                  <Checkbox
                                    checked={!!checkedPermissions[perm.id]}
                                    onChange={e => handleCheckboxChange(perm.id, e.target.checked)}
                                    size="small"
                     
                                    sx={{
                                      
                                      color: '#212B36',
                                      '&.Mui-checked': {
                                        color: '#F8991D',
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography className="commonColor" variant="body2"  sx={{ fontSize: '12px'}}>
                                    {perm.label}
                                  </Typography>
                                }
                                sx={{ m: 0 }}
                              />
                            ))}
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Box>
                  ))}
                </Paper>
                <Box sx={{ borderTop: '1px solid rgba(145, 158, 171, 0.16)', pt: 2, display: 'flex', justifyContent: 'end' }}>
                  <Button
                    variant="contained"
                    className="button-variant-contained"
              
                    onClick={handleUpdate}
                    disabled={!roleId}
                  >
                    Update Permissions
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </Box>

    </>
  );
};

export default ChangePermissions;
